/**
 * Protected Route Component
 * Wrapper component that requires authentication
 * 
 * FINLIST AUTH RULES:
 * - Unauthenticated users are redirected to /welcome (NOT /signin)
 * - Authenticated users stay on their current route (refresh continuity)
 * - Sign-in pages are accessed via explicit user action (CTA click)
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

/**
 * ProtectedRoute - Requires user authentication
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components to render if authenticated
 * @param {string} props.redirectTo - Path to redirect to if not authenticated (default: /welcome)
 */
const ProtectedRoute = ({ 
  children, 
  redirectTo = '/welcome' 
}) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 transition-colors">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Redirect to welcome page if not authenticated
  // User can then choose to sign in from the welcome page
  if (!currentUser) {
    return (
      <Navigate 
        to={redirectTo} 
        state={{ from: location.pathname }}
        replace 
      />
    );
  }

  // Render children if authenticated
  return children;
};

/**
 * PublicRoute - Redirects authenticated users away
 * 
 * FINLIST AUTH RULES:
 * - Authenticated users are ALWAYS redirected to /dashboard
 * - We do NOT redirect back to previous route after sign-in
 * - This ensures a clear "entry into the app" experience
 * 
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components to render if not authenticated
 * @param {string} props.redirectTo - Path to redirect to if authenticated (default: /dashboard)
 */
export const PublicRoute = ({ 
  children, 
  redirectTo = '/dashboard' 
}) => {
  const { currentUser, loading } = useAuth();

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 transition-colors">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // RULE 4: Always redirect authenticated users to dashboard (not previous route)
  if (currentUser) {
    return <Navigate to={redirectTo} replace />;
  }

  // Render children if not authenticated
  return children;
};

export default ProtectedRoute;